/**
 * How a price change on an existing order is settled, and which settlements an admin is
 * allowed to pick.
 *
 * Shared by change-voucher and change-package because the backend does not distinguish
 * them: both endpoints build the same request and hand it to the same
 * `settleUpAfterTotalChange`. Two copies of this table lived side by side and had to be
 * kept in step by hand; there is only one now.
 */

/**
 * Each preset is a name for a set of raw flags the backend takes. `expiry_minutes` is
 * added separately, by whoever assembles the request.
 *
 * `enable_payment_gateway` is stated in both directions on purpose — the backend's
 * `resolveGatewayMode` lets a stated value win over its PAYMENT_GATEWAY_ENABLE feature
 * flag, and picking "bill via gateway" here is meant to mean exactly that, flag or no
 * flag.
 */
export const SETTLEMENT_PRESETS = {
	gateway: { bypass_payment: false, enable_payment_gateway: true },
	manual: { bypass_payment: false, enable_payment_gateway: false },
	received: { bypass_payment: false, auto_complete_payment: true },
	refund_due: { bypass_payment: false },
	refunded: { bypass_payment: false, auto_complete_payment: true },
} as const;

export type SettlementPreset = keyof typeof SETTLEMENT_PRESETS;

/** Money comes in. */
export const CHARGE_PRESETS: SettlementPreset[] = [
	"gateway",
	"manual",
	"received",
];

/**
 * All that can happen when the total drops on an order with no cash to give back: the
 * backend re-prices the open payment row and re-issues its link. A module-level constant
 * rather than a fresh array, so callers can compare identity and use it as a hook dep.
 */
export const REBILL_PRESETS: SettlementPreset[] = ["gateway", "manual"];

/** Money goes out. Only reachable when the order actually holds money — see `presetsFor`. */
export const REFUND_PRESETS: SettlementPreset[] = ["refund_due", "refunded"];

/**
 * Books money as moved without any proof. No role gates these, so the brakes are social:
 * a written reason and a confirmation that names the consequence.
 */
export const RISKY_PRESETS: SettlementPreset[] = ["received", "refunded"];

/**
 * Whether the preset leaves a payment row an admin can still act on. Read off the flags
 * rather than kept as a second list: a row stays open exactly when the payment is neither
 * skipped nor booked as already settled — the same rule `Payment#settleable?` applies on
 * the Rails side.
 *
 * "Issue a payment link" counts. The patient pays it, but the link can fail to issue, and
 * the money can still arrive by transfer — both leave an admin holding the row.
 */
export function leavesPaymentOpen(preset: SettlementPreset): boolean {
	const flags: Record<string, boolean | undefined> = SETTLEMENT_PRESETS[preset];
	return !flags.bypass_payment && !flags.auto_complete_payment;
}

/** Money is carried as a float. Sub-rupiah drift is not a real difference. */
const EPSILON = 0.5;

/**
 * Which settlements this order may be given, judged in money rather than in status names.
 *
 * The backend does NOT branch on the sign of the change first — `settleUpAfterTotalChange`
 * branches on whether any cash has ever settled. An order that has received nothing takes
 * the re-bill path whatever the total does: its open payment row is re-priced in place and
 * its gateway invoice re-issued. `auto_complete_payment` on that path stamps a row nobody
 * paid as COMPLETED, which reconciles `paid_amount` up to the new total and leaves an
 * unpaid order looking paid and scheduled. So the refund presets are offered only when
 * there is genuinely something to give back.
 *
 * Stating the rule as "does the cash on hand exceed what the order will now cost" answers
 * UNPAID and PARTIALLY_PAID in one line: a zero-cash order can never reach the refund list,
 * and a part-paid order reaches it exactly when it has overshot.
 *
 * `delta` is null when no direction can be stated honestly yet (an apply with no voucher
 * picked). That is not the same as zero, but for this decision both bill rather than refund.
 */
export function presetsFor({
	delta,
	settledCash,
	currentTotal,
}: {
	/** How the total moves, positive when it goes UP. Null when nothing has been chosen. */
	delta: number | null;
	/** Cash actually received — `order.paidAmount`, maintained by the DB trigger. */
	settledCash: number;
	currentTotal: number;
}): SettlementPreset[] {
	const newTotal = currentTotal + (delta ?? 0);

	if (settledCash - newTotal > EPSILON) return REFUND_PRESETS;

	// The total drops but there is nothing to hand back. All that can happen is a re-bill,
	// so only the two settlements that re-bill are offered. "received" is deliberately
	// absent: on this path it would mark a never-paid row settled, which is the exact
	// accident this rule exists to prevent.
	if (delta != null && delta < 0) return REBILL_PRESETS;

	return CHARGE_PRESETS;
}

/**
 * What a package swap would do to the order's TOTAL, which is not what it does to the
 * package's list price.
 *
 * The two live in different bases and must not be added to each other: a package is
 * quoted at list price, while `total_amount` has already had the discount taken off and
 * the tax put on. Subtracting a full list-price difference from a discounted total
 * understates the new total, and understating it is the dangerous direction — it makes
 * the cash on hand look like it overshoots, and offers a refund nobody is owed.
 *
 * The discount is carried across unchanged because it is stored money, not a rule the
 * order re-evaluates — see docs/adr/0006. It is clamped at the new subtotal: a discount
 * bigger than what is left cannot push a total below zero.
 */
export function estimateRepricedTotalDelta({
	subtotalDelta,
	subtotal,
	discountAmount,
	taxPercentage,
	currentTotal,
}: {
	/** How the package list price moves, positive when it goes UP. */
	subtotalDelta: number;
	subtotal: number;
	discountAmount: number;
	taxPercentage: number;
	currentTotal: number;
}): number {
	const newSubtotal = subtotal + subtotalDelta;
	const taxed =
		Math.max(0, newSubtotal - discountAmount) * (1 + taxPercentage / 100);
	return taxed - currentTotal;
}
